import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { sendQuote, resendQuote } from "@/lib/quotes";
import { toast } from "sonner";

type Props = {
  quoteId: number;
  status: string;
  defaultCustomerEmail?: string;
  convertedToJobId?: number | null;
};

export function QuoteSendButton({ quoteId, status, defaultCustomerEmail, convertedToJobId }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultCustomerEmail ?? "");

  const isDraft = status === "Draft";
  const isResendable = status === "Sent" || (status === "Approved" && !convertedToJobId);
  if (!isDraft && !isResendable) return null;

  const label = isDraft ? "Send" : "Resend";

  const mutation = useMutation({
    mutationFn: () =>
      isDraft
        ? sendQuote(quoteId, { customerEmail: email })
        : resendQuote(quoteId, { customerEmail: email }),
    onSuccess: (result) => {
      toast.success(`${label} succeeded — version ${result.version}`);
      qc.invalidateQueries({ queryKey: ["quote", quoteId] });
      setOpen(false);
    },
    onError: (e: unknown) => toast.error(`${label} failed: ${(e as Error).message}`),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={isDraft ? "default" : "secondary"}>{label}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{label} quote to customer</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="customer-email">Customer email</Label>
          <Input
            id="customer-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!email || mutation.isPending}>
            {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
